import classnames from 'classnames';
import { IconPencil } from '../../../../../ui/Icons/IconPencil';
import React, { ReactNode, useState } from 'react';
import { IconSpinner } from '../../../../../ui/Icons/IconSpinner';
import { IconWarning } from '../../../../../ui/Icons/IconWarning';
import { IconSuccess } from '../../../../../ui/Icons/IconSuccess';
import './MsbInput.scss';

export type MsbActivationInputState = 'success' | 'error';
interface IInputHelperText {
  error?: string;
  info?: string;
}

interface IProps {
  id: string;
  name: string;
  label?: string;
  addonRight?: ReactNode;
  disabled?: boolean;
  required?: boolean;
  inputState?: MsbActivationInputState;
  helperText?: IInputHelperText;
  loading?: boolean;
  placeholder?: string;
  ref?: (a: any) => any;
  value?: string;
  onBlur?: (value: string) => void;
  onChange?: (value: string) => void;
}

export const MsbInputText = (formProps: IProps) => {
  const {
    id,
    name,
    label,
    addonRight,
    disabled,
    required,
    inputState,
    helperText,
    loading,
    placeholder,
    ref,
    value,
    onBlur,
    onChange
  } = formProps;

  const [isUpdate, setIsUpdate] = useState(false);
  const [isFocus, setIsFocus] = useState(false);

  const hasInfoHelperText: boolean = 
    !!(helperText && helperText.info && helperText.info !== '');

  const hasError = () => {
    return inputState === 'error' && !disabled;
  };

  const hasSuccess = () => {
    return inputState === 'success' && !disabled;
  };

  const hasAddonRight = () => {
    return !!addonRight;
  };

  const hasDisabled = () => {
    return !!disabled;
  };

  const getIcon = () => {
    if (loading) {
      return <IconSpinner size={16} />;
    }

    if (hasError()) {
      return <IconWarning />;
    }

    if (!!value && !isUpdate) {
      return <IconSuccess />;
    }
    return null;
  };

  const handleChange = (event: any) => {
    event.preventDefault();
    if (isFocus) {
      setIsUpdate(true);
    }
    if (onChange) {
      onChange(event.target.value as string);
    }
  };

  const handleBlur = (event: any) => {
    event.preventDefault();
    setIsFocus(false);
    setIsUpdate(false);
    if (onBlur) {
      onBlur(event.target.value as string);
    }
  };

  const handleFocus = (event: any) => {
    setIsFocus(true);
  };

  return (
    <div
      className={classnames({
        'msb-activation-form-group--with-error': hasError(),
        'msb-activation-form-group--with-success': hasSuccess(),
        'msb-activation-form-group--with-addon-right': hasAddonRight(),
        'msb-activation-form-group--disabled': hasDisabled()
      })}
    >
      <div className="msb-activation-form-group__label">
        {label && (
          <label>
            {label} {required && ' *'}{' '}
          </label>
        )}
      </div>
      <div className="msb-activation-form-group__input-and-icon-container">
        <div className="msb-activation-form-group__input-and-addon-right-container">
          <div className="msb-activation-form-group__input-container">
            <input
              id={id}
              type="text"
              name={name}
              value={value}
              ref={ref}
              className={classnames('msb-activation-form-group__input', {
                'msb-activation-form-group__input--error': hasError(),
                'msb-activation-form-group__input--success': hasSuccess()
              })}
              onChange={event => handleChange(event)}
              onBlur={event => handleBlur(event)}
              onFocus={event => handleFocus(event)}
              disabled={disabled}
              placeholder={placeholder}
            />
            {!hasDisabled() && (
              <IconPencil className="msb-activation-form-group__edit-icon" />
            )}
          </div>
          {addonRight && (
            <div className="msb-activation-form-group__addon-right">
              {addonRight}
            </div>
          )}
        </div>
        <div className="msb-activation-form-group__icon">{getIcon()}</div>
      </div>
      {helperText && (
        <div className="msb-activation-form-group__helper-text">
          {!hasError() && 
            hasInfoHelperText && (
              <span className="msb-activation-form-group__helper-text--info">
                { helperText.info }
              </span>
            )
          }

          {hasError() &&
            helperText.error && (
              <span className="msb-activation-form-group__helper-text--error">
                {helperText.error}
              </span>
            )}
        </div>
      )}
    </div>
  );
};
